import React from 'react'
import { DcButton, DcInput, PixelTable } from 'pixel-base'
import { Invoice } from '../types/finance'
import FinanceActionBar from './foundation/FinanceActionBar'
import FinanceCard from './foundation/FinanceCard'
import FinanceInlineAlert from './foundation/FinanceInlineAlert'
import formatFinanceDate from '../utils/formatFinanceDate'

interface Props {
  title?: string
  rows: Invoice[]
  availableAmount: number
  onSubmit: (
    _allocations: { invoice_id: number; allocated_amount: number }[],
  ) => void
  isLoading?: boolean
}

const AllocationWorkbench: React.FC<Props> = ({
  title = 'Allocation Workbench',
  rows,
  availableAmount,
  onSubmit,
  isLoading = false,
}) => {
  const [allocations, setAllocations] = React.useState<Record<number, string>>(
    {},
  )
  const [validation, setValidation] = React.useState<string>('')
  const totalAllocated = Object.values(allocations).reduce(
    (sum, value) => sum + Number(value || 0),
    0,
  )
  const remaining = Math.max(0, Number(availableAmount || 0) - totalAllocated)

  const setValue = (invoiceId: number, value: string) => {
    setValidation('')
    setAllocations((current) => ({ ...current, [invoiceId]: value }))
  }

  const submit = () => {
    const payload = rows
      .map((row) => ({
        invoice_id: row.id,
        allocated_amount: Number(allocations[row.id] || 0),
        max: Number(row.amount_residual || 0),
      }))
      .filter(
        (row) => row.allocated_amount > 0 && row.allocated_amount <= row.max,
      )

    if (!payload.length) {
      setValidation(
        'Enter at least one valid allocation amount before applying.',
      )
      return
    }

    if (
      payload.reduce((sum, row) => sum + row.allocated_amount, 0) >
      Number(availableAmount || 0)
    ) {
      setValidation('Allocated total cannot exceed the payment open amount.')
      return
    }

    onSubmit(
      payload.map(({ invoice_id, allocated_amount }) => ({
        invoice_id,
        allocated_amount,
      })),
    )
  }

  return (
    <FinanceCard
      title={title}
      description={`Available: ${
        availableAmount || 0
      } | Allocated: ${totalAllocated} | Remaining: ${remaining}`}
    >
      {validation && (
        <div style={{ marginBottom: 12 }}>
          <FinanceInlineAlert tone="warning" message={validation} />
        </div>
      )}
      <PixelTable
        columns={[
          { name: 'Number', selector: (row) => row.number || `DOC-${row.id}` },
          {
            name: 'Partner',
            selector: (row) => row.partner?.name || row.partner_id || '--',
          },
          {
            name: 'Due Date',
            selector: (row) => formatFinanceDate(row.due_date),
          },
          { name: 'Total', selector: (row) => row.amount_total ?? '--' },
          { name: 'Residual', selector: (row) => row.amount_residual ?? '--' },
          { name: 'State', selector: (row) => row.state || '--' },
          {
            name: 'Allocate',
            selector: (row) => (
              <DcInput
                type="number"
                value={allocations[row.id] || ''}
                placeholder="0"
                onChange={(e) => setValue(row.id, e.target.value)}
              />
            ),
          },
        ]}
        data={rows}
        pagination
        searchable
      />
      <FinanceActionBar
        $align="end"
        style={{ marginTop: 16 } as React.CSSProperties}
      >
        <DcButton
          onClick={submit}
          disabled={
            isLoading ||
            totalAllocated <= 0 ||
            totalAllocated > Number(availableAmount || 0)
          }
        >
          Apply Allocation
        </DcButton>
      </FinanceActionBar>
    </FinanceCard>
  )
}

export default AllocationWorkbench
